import React, { useState, useEffect } from 'react';

// NumberInput extracted outside to prevent focus loss on parent re-render
const NumberInput = ({ label, name, value, onChange, onBlur, placeholder = "0", hideCurrency = false }) => {
    const handleKeyDown = (e) => {
        // Allow only numbers, backspace, delete, arrows, tab
        const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
        if (allowedKeys.includes(e.key)) return;
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };
    
    return (
        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block' }}>{label}</label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
                {!hideCurrency && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', zIndex: 1 }}>₹</span>}
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    style={{ width: '100%', padding: hideCurrency ? '8px 10px' : '8px 10px 8px 25px', borderRadius: '6px', border: '1px solid #d1d5db', boxSizing: 'border-box' }}
                />
            </div>
        </div>
    );
};

const Step6LandGold = ({ data, updateData, nextStep, prevStep, isLastStep, isProcessing, formData }) => {
    const [formDataState, setFormDataState] = useState({
        // Land Assets
        hasLand: data?.hasLand || false,
        landValue: data?.landValue || '',
        landTypes: data?.landTypes || [],
        landLocationCategory: data?.landLocationCategory || 'village',
        landArea: data?.landArea || '',
        landAreaUnit: data?.landAreaUnit || 'sq_ft',
        
        // Gold Assets
        hasGold: data?.hasGold || false,
        goldValue: data?.goldValue || '',
        goldWeight: data?.goldWeight || '',
        goldType: data?.goldType || 'jewelry',
        
        // Investment Assets (New)
        sipAmount: data?.sipAmount || '',
        ppfAmount: data?.ppfAmount || '',
        fdRdAmount: data?.fdRdAmount || '',
        
        // Old Liabilities (New)
        hasOutstandingLoans: data?.hasOutstandingLoans || false,
        homeLoanOutstanding: data?.homeLoanOutstanding || '',
        personalLoanOutstanding: data?.personalLoanOutstanding || '',
        personalLoanRate: data?.personalLoanRate || '14',
        carLoanOutstanding: data?.carLoanOutstanding || '',
        otherLoanOutstanding: data?.otherLoanOutstanding || '',
        
        // Calculated Values
        totalPhysicalAssets: data?.totalPhysicalAssets || 0,
        totalInvestmentAssets: data?.totalInvestmentAssets || 0,
        totalLiabilities: data?.totalLiabilities || 0,
        netWorth: data?.netWorth || 0,
        liquidityRatio: data?.liquidityRatio || 0,
        assetCoverageRatio: data?.assetCoverageRatio || 0,
    });

    const [expandedSections, setExpandedSections] = useState({
        land: true,
        gold: true,
        investments: true,
        liabilities: true,
    });

    // Calculate financial metrics only when leaving the input field (onBlur)
    // This prevents re-renders while typing

    const calculateMetrics = () => {
        // Get data from previous forms
        const salaryIncome = formData?.salaryIncome || {};
        const salaryInvestment = formData?.salaryInvestment || {};
        const salaryExpenses = formData?.salaryExpenses || {};
        
        // ============================================
        // FINANCIAL ASSETS (Monthly values from form)
        // ============================================
        const emergencyFund = parseFloat(salaryInvestment?.emergencyFund) || 0;
        const liquidFunds = parseFloat(salaryInvestment?.liquidFunds) || 0;
        const sip = parseFloat(salaryInvestment?.sip) || 0;
        const ppfMonthly = parseFloat(salaryIncome?.ppf) || 0;
        const epfMonthly = parseFloat(salaryIncome?.epf) || 0;
        const vpfMonthly = parseFloat(salaryIncome?.vpf) || 0;
        
        // Annual financial assets
        const totalFinancialAssetsAnnual = (ppfMonthly + epfMonthly + vpfMonthly + sip + emergencyFund + liquidFunds) * 12;
        
        // ============================================
        // PHYSICAL ASSETS (From current form)
        // ============================================
        const landValue = formDataState.hasLand ? (parseFloat(formDataState.landValue) || 0) : 0;
        const goldValue = formDataState.hasGold ? (parseFloat(formDataState.goldValue) || 0) : 0;
        const totalPhysicalAssets = landValue + goldValue;
        
        // ============================================
        // INVESTMENT ASSETS (From current form - Step 6)
        // Note: SIP and PPF already counted in Financial Assets (monthly × 12)
        // Only FD/RD is additional here
        // ============================================
        const fdRdAmount = parseFloat(formDataState.fdRdAmount) || 0;
        const totalInvestmentAssets = fdRdAmount;
        
        // ============================================
        // TOTAL ASSETS & LIABILITIES
        // ============================================
        const totalAssets = totalFinancialAssetsAnnual + totalPhysicalAssets + totalInvestmentAssets;
        
        // Liabilities calculation - from form input (old liabilities)
        const homeLoanOutstanding = formDataState.hasOutstandingLoans ? (parseFloat(formDataState.homeLoanOutstanding) || 0) : 0;
        const personalLoanOutstanding = formDataState.hasOutstandingLoans ? (parseFloat(formDataState.personalLoanOutstanding) || 0) : 0;
        const carLoanOutstanding = formDataState.hasOutstandingLoans ? (parseFloat(formDataState.carLoanOutstanding) || 0) : 0;
        const otherLoanOutstanding = formDataState.hasOutstandingLoans ? (parseFloat(formDataState.otherLoanOutstanding) || 0) : 0;
        const totalLiabilities = homeLoanOutstanding + personalLoanOutstanding + carLoanOutstanding + otherLoanOutstanding;
        
        // ============================================
        // A. NET WORTH CALCULATION
        // Formula: Net Worth = (Financial Assets + Physical Assets) - Liabilities
        // ============================================
        const netWorth = totalAssets - totalLiabilities;
        
        // ============================================
        // B. LIQUIDITY RATIO CALCULATION
        // Formula: Liquidity Ratio = Liquid Assets / Monthly Expenses
        // Note: Land & Gold are NOT liquid
        // ============================================
        const monthlyEssentials = parseFloat(salaryExpenses?.essentialExpenses) || 0;
        const monthlyLifestyle = parseFloat(salaryExpenses?.lifestyleExpenses) || 0;
        const totalMonthlyExpenses = monthlyEssentials + monthlyLifestyle || 50000;
        
        // Liquid assets only (excluding land, gold, and long-term investments)
        const liquidAssetsMonthly = emergencyFund + liquidFunds; // Only cash/liquid
        const liquidityRatio = totalMonthlyExpenses > 0 ? liquidAssetsMonthly / totalMonthlyExpenses : 0;
        
        // Emergency fund target (6 months)
        const emergencyFundTarget = totalMonthlyExpenses * 6;
        const emergencyFundGap = Math.max(0, emergencyFundTarget - emergencyFund);
        
        // ============================================
        // C. ASSET COVERAGE RATIO (For Debt Management)
        // Formula: Asset Coverage = Pledgeable Assets / High-Cost Debt
        // Banks lend: 50% against land, 70% against gold
        // ============================================
        const loanAgainstLand = landValue * 0.5; // 50% of land value
        const loanAgainstGold = goldValue * 0.7; // 70% of gold value
        const totalPledgeableAssets = loanAgainstLand + loanAgainstGold;
        
        const highCostDebt = personalLoanOutstanding;
        const assetCoverageRatio = highCostDebt > 0 ? totalPledgeableAssets / highCostDebt : 
                                   (totalPledgeableAssets > 0 ? Infinity : 0);
        
        // Potential interest savings
        const personalLoanRate = parseFloat(formDataState.personalLoanRate) || 14;
        const goldLoanRate = 9.5; // 9.5% typical
        const potentialInterestSavings = highCostDebt * ((personalLoanRate - goldLoanRate) / 100);
        
        // ============================================
        // D. RETIREMENT CORPUS PROJECTION
        // Physical assets appreciation (if held)
        // ============================================
        const yearsToRetirement = 28; // Assuming age 32 to 60
        const landAppreciationRate = 0.06; // 6% annual
        const goldAppreciationRate = 0.08; // 8% annual
        
        const projectedLandValue = landValue * Math.pow(1 + landAppreciationRate, yearsToRetirement);
        const projectedGoldValue = goldValue * Math.pow(1 + goldAppreciationRate, yearsToRetirement);
        const totalPhysicalAtRetirement = projectedLandValue + projectedGoldValue;
        
        // ============================================
        // UPDATE STATE WITH ALL CALCULATIONS
        // ============================================
        setFormDataState(prev => ({
            ...prev,
            // Asset Values
            totalFinancialAssets: totalFinancialAssetsAnnual,
            totalPhysicalAssets,
            totalInvestmentAssets,
            totalAssets,
            totalLiabilities,
            
            // Net Worth
            netWorth,
            netWorthStatus: netWorth >= 0 ? 'positive' : 'negative',
            
            // Liquidity
            liquidAssets: liquidAssetsMonthly,
            totalMonthlyExpenses,
            liquidityRatio,
            liquidityStatus: liquidityRatio >= 6 ? 'good' : liquidityRatio >= 3 ? 'moderate' : 'poor',
            emergencyFundTarget,
            emergencyFundGap,
            
            // Asset Coverage (Debt Management)
            loanAgainstLand,
            loanAgainstGold,
            totalPledgeableAssets,
            highCostDebt,
            assetCoverageRatio,
            potentialInterestSavings,
            
            // Retirement Projection
            projectedLandValue,
            projectedGoldValue,
            totalPhysicalAtRetirement,
        }));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormDataState(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleBlur = () => {
        calculateMetrics();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Capture latest values from DOM in case onBlur hasn't fired yet
        const form = e.target;
        const fd = new FormData(form);
        const latestData = { ...formDataState };
        
        const numericFields = ['landValue', 'goldValue', 'goldWeight', 'sipAmount', 'ppfAmount', 'fdRdAmount', 'homeLoanOutstanding', 'personalLoanOutstanding', 'carLoanOutstanding', 'otherLoanOutstanding'];
        numericFields.forEach(field => {
            if (fd.has(field)) {
                latestData[field] = fd.get(field);
            }
        });
        
        updateData(latestData);
        nextStep();
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };


    return (
        <div className="form-step">
            <h2 className="step-title">Part 4: Land & Gold Assets</h2>
            <p className="step-description">Physical assets for comprehensive net worth calculation</p>

            <form onSubmit={handleSubmit} className="diagnosis-form">
                
                {/* Land Assets Section */}
                <div className="expense-section" style={{ background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('land')}>
                        <span>1. Land Assets</span>
                        <span className="toggle-icon">{expandedSections.land ? '▼' : '▶'}</span>
                    </h4>
                    
                    {expandedSections.land && (
                        <>
                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="hasLand"
                                        checked={formDataState.hasLand}
                                        onChange={handleChange}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span>I own land (agricultural/urban/plot)</span>
                                </label>
                            </div>
                            
                            {formDataState.hasLand && (
                                <>
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Current Market Value of Land" 
                                        name="landValue" 
                                        value={formDataState.landValue}
                                        onBlur={handleBlur}
                                        placeholder="1500000"
                                    />
                                    
                                    <div className="form-group" style={{ marginBottom: '12px' }}>
                                        <label style={{ fontSize: '0.9rem', marginBottom: '8px', display: 'block' }}>Land Type (Select all that apply)</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {[
                                                { value: 'agricultural', label: 'Agricultural Land' },
                                                { value: 'residential_plot', label: 'Residential Plot' },
                                                { value: 'residential_flat', label: 'Residential Flat' },
                                                { value: 'commercial', label: 'Commercial Land' },
                                                { value: 'industrial', label: 'Industrial Land' },
                                            ].map((option) => (
                                                <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        value={option.value}
                                                        checked={formDataState.landTypes?.includes(option.value)}
                                                        onChange={(e) => {
                                                            const value = option.value;
                                                            const currentTypes = formDataState.landTypes || [];
                                                            if (e.target.checked) {
                                                                setFormDataState(prev => ({ ...prev, landTypes: [...currentTypes, value] }));
                                                            } else {
                                                                setFormDataState(prev => ({ ...prev, landTypes: currentTypes.filter(t => t !== value) }));
                                                            }
                                                        }}
                                                        style={{ width: '16px', height: '16px' }}
                                                    />
                                                    <span>{option.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="form-group" style={{ marginBottom: '12px' }}>
                                        <label style={{ fontSize: '0.9rem', marginBottom: '8px', display: 'block' }}>Land Area</label>
                                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                            <input
                                                type="number"
                                                name="landArea"
                                                value={formDataState.landArea}
                                                onChange={handleChange}
                                                placeholder="Enter area"
                                                style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                            />
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                                                    <input
                                                        type="radio"
                                                        name="landAreaUnit"
                                                        value="sq_ft"
                                                        checked={formDataState.landAreaUnit === 'sq_ft'}
                                                        onChange={handleChange}
                                                        style={{ width: '16px', height: '16px' }}
                                                    />
                                                    <span>Sq Ft</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.85rem' }}>
                                                    <input
                                                        type="radio"
                                                        name="landAreaUnit"
                                                        value="acres"
                                                        checked={formDataState.landAreaUnit === 'acres'}
                                                        onChange={handleChange}
                                                        style={{ width: '16px', height: '16px' }}
                                                    />
                                                    <span>Acres</span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="form-group" style={{ marginBottom: '12px' }}>
                                        <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block' }}>Location Type</label>
                                        <select
                                            name="landLocationCategory"
                                            value={formDataState.landLocationCategory}
                                            onChange={handleChange}
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                        >
                                            <option value="village">Village</option>
                                            <option value="1tier">1 Tier City</option>
                                            <option value="2tier">2 Tier City</option>
                                            <option value="metro">Metro City</option>
                                        </select>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Gold Assets Section */}
                <div className="expense-section" style={{ background: '#fffbeb', border: '1px solid #fcd34d' }}>
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('gold')}>
                        <span>2. Gold / Silver Assets</span>
                        <span className="toggle-icon">{expandedSections.gold ? '▼' : '▶'}</span>
                    </h4>
                    
                    {expandedSections.gold && (
                        <>
                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="hasGold"
                                        checked={formDataState.hasGold}
                                        onChange={handleChange}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span>I own gold (jewelry/coins/bars)</span>
                                </label>
                            </div>
                            
                            {formDataState.hasGold && (
                                <>
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Current Market Value (Gold / Silver)" 
                                        name="goldValue" 
                                        value={formDataState.goldValue}
                                        onBlur={handleBlur}
                                        placeholder="600000"
                                    />
                                    
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Total Weight (in grams)" 
                                        name="goldWeight" 
                                        value={formDataState.goldWeight}
                                        onBlur={handleBlur}
                                        placeholder="100"
                                        hideCurrency={true}
                                    />
                                    
                                    <div className="form-group" style={{ marginBottom: '12px' }}>
                                        <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block' }}>Asset Type</label>
                                        <select
                                            name="goldType"
                                            value={formDataState.goldType}
                                            onChange={handleChange}
                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                                        >
                                            <optgroup label="Gold Assets">
                                                <option value="jewelry">Gold Jewelry</option>
                                                <option value="coins">Gold Coins</option>
                                                <option value="bars">Gold Bars</option>
                                                <option value="sovereign_gold">Sovereign Gold Bonds</option>
                                            </optgroup>
                                            <optgroup label="Silver Assets">
                                                <option value="silver_jewelry">Silver Jewelry</option>
                                                <option value="silver_coins">Silver Coins</option>
                                                <option value="silver_bars">Silver Bars/Items</option>
                                            </optgroup>
                                        </select>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Investment Assets Section */}
                <div className="expense-section" style={{ background: '#eff6ff', border: '1px solid #93c5fd' }}>
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('investments')}>
                        <span>3. Investment Assets</span>
                        <span className="toggle-icon">{expandedSections.investments ? '▼' : '▶'}</span>
                    </h4>
                    
                    {expandedSections.investments && (
                        <>
                            <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                label="SIP (Systematic Investment Plan) Amount (Total investment  till date)" 
                                name="sipAmount" 
                                value={formDataState.sipAmount}
                                onBlur={handleBlur}
                                placeholder="10000"
                            />
                            
                            <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                label="PPF (Public Provident Fund) Balance" 
                                name="ppfAmount" 
                                value={formDataState.ppfAmount}
                                onBlur={handleBlur}
                                placeholder="500000"
                            />
                            
                            <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                label="FD/RD (Fixed/Recurring Deposit) Amount" 
                                name="fdRdAmount" 
                                value={formDataState.fdRdAmount}
                                onBlur={handleBlur}
                                placeholder="200000"
                            />
                        </>
                    )}
                </div>

                {/* Old Liabilities Section */}
                <div className="expense-section" style={{ background: '#fef2f2', border: '1px solid #fca5a5' }}>
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('liabilities')}>
                        <span>4. Outstanding Loans (Liabilities)</span>
                        <span className="toggle-icon">{expandedSections.liabilities ? '▼' : '▶'}</span>
                    </h4>
                    
                    {expandedSections.liabilities && (
                        <>
                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        name="hasOutstandingLoans"
                                        checked={formDataState.hasOutstandingLoans}
                                        onChange={handleChange}
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                    <span>I have outstanding loans</span>
                                </label>
                            </div>
                            
                            {formDataState.hasOutstandingLoans && (
                                <>
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Home Loan Outstanding" 
                                        name="homeLoanOutstanding" 
                                        value={formDataState.homeLoanOutstanding}
                                        onBlur={handleBlur}
                                        placeholder="15000000"
                                    />
                                    
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Personal Loan Outstanding" 
                                        name="personalLoanOutstanding" 
                                        value={formDataState.personalLoanOutstanding}
                                        onBlur={handleBlur}
                                        placeholder="200000"
                                    />

                                    
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Car Loan Outstanding" 
                                        name="carLoanOutstanding" 
                                        value={formDataState.carLoanOutstanding}
                                        onBlur={handleBlur}
                                        placeholder="500000"
                                    />
                                    
                                    <NumberInput onChange={(e) => handleChange({ target: { name: e.target.name, value: e.target.value, type: "text" } })} 
                                        label="Other Loan Outstanding" 
                                        name="otherLoanOutstanding" 
                                        value={formDataState.otherLoanOutstanding}
                                        onBlur={handleBlur}
                                        placeholder="100000"
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="form-navigation" style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                    <button 
                        type="button" 
                        onClick={prevStep}
                        className="btn btn-outline"
                        style={{ flex: 1 }}
                    >
                        ← Back
                    </button>
                    <button 
                        type="submit"
                        className="btn btn-primary"
                        disabled={isProcessing}
                        style={{ flex: 2, background: '#059669', color: 'white', padding: '12px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                    >
                        {isProcessing ? 'Processing...' : (isLastStep ? 'Complete' : 'Next Step →')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Step6LandGold;
